import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/utils';

const ToastProvider = ToastPrimitives.Provider;
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn('fixed bottom-4 right-4 z-[100] flex max-h-screen w-full max-w-[380px] flex-col gap-2', className)}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

export type ToastVariant = 'default' | 'success' | 'destructive' | 'info';

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & { variant?: ToastVariant }
>(({ className, variant = 'default', ...props }, ref) => {
  const variantClasses = {
    default: 'bg-white border-gray-200',
    success: 'bg-white border-green-200',
    destructive: 'bg-white border-red-200',
    info: 'bg-white border-blue-200',
  };
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(
        'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-2xl border p-4 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
        variantClasses[variant],
        className
      )}
      {...props}
    />
  );
});
Toast.displayName = 'Toast';

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn('absolute right-3 top-3 rounded-md p-0.5 text-gray-400 hover:text-gray-600', className)}
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = 'ToastClose';

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('text-sm font-semibold text-gray-900', className)} {...props} />
));
ToastTitle.displayName = 'ToastTitle';

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn('text-sm text-gray-600', className)} {...props} />
));
ToastDescription.displayName = 'ToastDescription';

type ToastIconProps = { variant: ToastVariant };
const ToastIcon = ({ variant }: ToastIconProps) => {
  if (variant === 'success') return <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />;
  if (variant === 'destructive') return <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />;
  if (variant === 'info') return <Info className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />;
  return null;
};

export { ToastProvider, ToastViewport, Toast, ToastClose, ToastTitle, ToastDescription, ToastIcon };
export type { ToastPrimitives };
